import {
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useState, useMemo, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { useMsal } from "@azure/msal-react";
import { DataTableShell } from "../components/table/DataTableShell";

// ---------------------------------------------------------------------------
// Helpers (mirrored from Transactions.tsx)
// ---------------------------------------------------------------------------

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDeletedAt(ms: number) {
  return new Date(ms).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Category labels
// ---------------------------------------------------------------------------

const DELETION_CATEGORY_LABELS: Record<string, string> = {
  nonexistent_transaction: "Nonexistent",
  duplicate: "Duplicate",
  incorrect_value: "Incorrect value",
  ocr_error: "OCR error",
  invalid_vendor: "Invalid vendor",
  other: "Other",
};

const DELETION_CATEGORY_COLORS: Record<string, "error" | "warning" | "info" | "default" | "primary" | "secondary" | "success"> = {
  nonexistent_transaction: "error",
  duplicate: "warning",
  incorrect_value: "warning",
  ocr_error: "info",
  invalid_vendor: "primary",
  other: "default",
};

const CATEGORY_OPTIONS = [
  { value: "nonexistent_transaction", label: "Nonexistent transaction" },
  { value: "duplicate", label: "Duplicate" },
  { value: "incorrect_value", label: "Incorrect value" },
  { value: "ocr_error", label: "OCR error" },
  { value: "invalid_vendor", label: "Invalid vendor" },
  { value: "other", label: "Other" },
];

// ---------------------------------------------------------------------------
// Snackbar state type
// ---------------------------------------------------------------------------

interface SnackState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

// ---------------------------------------------------------------------------
// TransactionsTrash
// ---------------------------------------------------------------------------

export function TransactionsTrash() {
  // --- MSAL identity (verbatim from CardsAdminPage.tsx) ---
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    "";

  // Role check — admin only for restore
  const roleEmailCandidates = [
    account?.username ?? "",
    claimStr("preferred_username"),
    claimStr("upn"),
    claimStr("email"),
  ].filter((e) => e && e.includes("@"));
  const userRecord = useQuery(
    api.users.findByAnyEmail,
    roleEmailCandidates.length > 0 ? { emails: roleEmailCandidates } : "skip",
  );
  const role = userRecord?.role ?? "viewer";
  const isAdmin = role === "admin";

  // --- Filter state ---
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const hasFilters = !!(categoryFilter || searchTerm.trim() || fromDate || toDate);

  const clearFilters = useCallback(() => {
    setCategoryFilter("");
    setSearchTerm("");
    setFromDate("");
    setToDate("");
  }, []);

  // Convert date strings to epoch ms for query args
  const fromMs = useMemo(
    () => (fromDate ? new Date(fromDate + "T00:00:00").getTime() : undefined),
    [fromDate],
  );
  const toMs = useMemo(
    () => (toDate ? new Date(toDate + "T23:59:59.999").getTime() : undefined),
    [toDate],
  );

  // --- Data query ---
  const deletedRaw = useStableQuery(
    api.transactions.listDeleted,
    {
      category: categoryFilter ? (categoryFilter as any) : undefined,
      search: searchTerm.trim() || undefined,
      fromMs,
      toMs,
    },
    [],
  );
  const loading = deletedRaw === undefined;
  const deletedRows = (deletedRaw ?? []) as any[];

  // Rows with `id` field for DataGrid
  const rows = useMemo(
    () => deletedRows.map((r) => ({ ...r, id: r._id })),
    [deletedRows],
  );

  // --- Restore mutation ---
  const restoreMutation = useMutation(api.transactions.restore);

  // --- Snackbar ---
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: "",
    severity: "success",
  });
  const showSnack = useCallback(
    (message: string, severity: "success" | "error") => {
      setSnack({ open: true, message, severity });
    },
    [],
  );

  const handleRestore = useCallback(
    async (row: any) => {
      try {
        await restoreMutation({ id: row._id, restoredBy: email });
        showSnack(`Transaction restored successfully.`, "success");
      } catch (e: any) {
        showSnack(e?.message ?? "Restore failed.", "error");
      }
    },
    [restoreMutation, email, showSnack],
  );

  // --- Columns ---
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "date",
        headerName: "Date",
        width: 110,
        sortable: true,
        renderCell: (params: GridRenderCellParams) =>
          params.value ? formatDate(params.value as string) : "—",
      },
      {
        field: "vendorName",
        headerName: "Vendor",
        flex: 1,
        minWidth: 140,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value ?? "—"}
          </Typography>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 120,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: (params: GridRenderCellParams) => {
          const val = params.value as number;
          return (
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: val >= 0 ? "var(--c-metric-success)" : "var(--c-text-primary)" }}
            >
              {formatCurrency(val)}
            </Typography>
          );
        },
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 180,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}
            title={params.value ?? ""}
          >
            {params.value ?? "—"}
          </Typography>
        ),
      },
      {
        field: "deletionCategory",
        headerName: "Category",
        width: 150,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const cat = params.value as string | undefined;
          if (!cat) return <Typography variant="body2" color="text.secondary">—</Typography>;
          return (
            <Chip
              size="small"
              label={DELETION_CATEGORY_LABELS[cat] ?? cat}
              color={DELETION_CATEGORY_COLORS[cat] ?? "default"}
            />
          );
        },
      },
      {
        field: "deletionReason",
        headerName: "Justification",
        flex: 2,
        minWidth: 180,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%", color: "text.secondary" }}
            title={params.value ?? ""}
          >
            {params.value ?? "—"}
          </Typography>
        ),
      },
      {
        field: "deletedBy",
        headerName: "Deleted by",
        width: 180,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value ?? "—"}
          </Typography>
        ),
      },
      {
        field: "deletedAt",
        headerName: "Deleted at",
        width: 190,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const ms = params.value as number | undefined;
          if (!ms) return <Typography variant="body2" color="text.secondary">—</Typography>;
          return (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {formatDeletedAt(ms)}
            </Typography>
          );
        },
      },
      {
        field: "_restore",
        headerName: "",
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as any;
          if (isAdmin) {
            return (
              <Tooltip title="Restore this transaction (admin only)">
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<RestoreFromTrashIcon fontSize="small" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(row);
                  }}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  Restore
                </Button>
              </Tooltip>
            );
          }
          return (
            <Tooltip title="Only admins can restore deleted transactions">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestoreFromTrashIcon fontSize="small" />}
                  disabled
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  Restore
                </Button>
              </span>
            </Tooltip>
          );
        },
      },
    ],
    [isAdmin, handleRestore],
  );

  // --- Filter bar ---
  const filterBar = (
    <>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Category</InputLabel>
        <Select
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          displayEmpty
        >
          <MenuItem value="">All categories</MenuItem>
          {CATEGORY_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        label="Justification search"
        placeholder="Search justification text…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ minWidth: 240 }}
      />

      <TextField
        size="small"
        label="Deleted from"
        type="date"
        value={fromDate}
        onChange={(e) => setFromDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />

      <TextField
        size="small"
        label="Deleted to"
        type="date"
        value={toDate}
        onChange={(e) => setToDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />

      {hasFilters && (
        <Button
          size="small"
          variant="outlined"
          onClick={clearFilters}
          sx={{ textTransform: "none", flexShrink: 0 }}
        >
          Clear filters
        </Button>
      )}
    </>
  );

  return (
    <>
      <DataTableShell
        data-tour="page-transactions-trash"
        title="Transaction Trash"
        subtitle="Soft-deleted transactions. Admins can restore any row; deleted rows are excluded from all other views."
        rows={rows}
        columns={columns}
        loading={loading}
        itemLabel="deleted transaction"
        emptyTitle="Trash is empty"
        emptyDescription="No transactions have been deleted yet."
        emptyIcon={<DeleteSweepIcon sx={{ fontSize: 32, color: "var(--c-link-accent)" }} />}
        filterBar={filterBar}
        onResetFilters={hasFilters ? clearFilters : undefined}
        gridProps={{
          initialState: {
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "deletedAt", sort: "desc" }] },
          },
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TransactionsTrash;
